import { useEffect, useRef } from 'react';
import {
  motion,
  useMotionTemplate,
  useMotionValue,
  useScroll,
  useSpring,
  useTransform,
} from 'framer-motion';

// Layered leather backdrop: bespoke macro-grain photography (generated for the
// house), slow parallax + breathing drift, and a LIGHT-REACTIVE rake — a
// brighter copy of the same hide is revealed through a soft elliptical mask
// that follows the pointer, so the grain looks physically lit. No glows.

const TEXTURES = {
  grain: '/textures/leather-grain.jpg',
  stitch: '/textures/leather-stitch.jpg',
};

const GRAIN_SVG =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='180' height='180' filter='url(%23n)' opacity='0.55'/%3E%3C/svg%3E\")";

export const LeatherBackdrop = ({
  variant = 'grain',
  intensity = 0.4,
  drift = 14,
  className = '',
  fade = true,
  reactive = true,
}) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const y = useTransform(scrollYProgress, [0, 1], [`-${drift}%`, `${drift}%`]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.22, 1.12, 1.24]);

  // Pointer-driven light rake (percent of the backdrop box).
  const px = useMotionValue(38);
  const py = useMotionValue(34);
  const sx = useSpring(px, { stiffness: 70, damping: 22, mass: 0.6 });
  const sy = useSpring(py, { stiffness: 70, damping: 22, mass: 0.6 });
  const rake = useMotionTemplate`radial-gradient(20% 28% at ${sx}% ${sy}%, #000 0%, rgba(0,0,0,0.45) 40%, transparent 72%)`;

  useEffect(() => {
    if (!reactive) return;
    if (!window.matchMedia('(pointer: fine)').matches) return;
    const move = (e) => {
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (r.bottom < 0 || r.top > window.innerHeight) return;
      px.set(((e.clientX - r.left) / r.width) * 100);
      py.set(((e.clientY - r.top) / r.height) * 100);
    };
    window.addEventListener('mousemove', move, { passive: true });
    return () => window.removeEventListener('mousemove', move);
  }, [px, py, reactive]);

  const edgeMask = fade
    ? 'radial-gradient(125% 100% at 50% 38%, #000 40%, transparent 100%)'
    : undefined;
  const src = TEXTURES[variant] || TEXTURES.grain;

  return (
    <div
      ref={ref}
      aria-hidden="true"
      className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}
      data-testid={`leather-backdrop-${variant}`}
    >
      {/* base hide — parallax + slow breathing drift */}
      <motion.div
        className="absolute inset-0"
        style={{ y, scale, maskImage: edgeMask, WebkitMaskImage: edgeMask }}
      >
        <motion.img
          src={src}
          alt=""
          className="w-full h-full object-cover"
          style={{ opacity: intensity, filter: 'grayscale(1) contrast(1.08)' }}
          animate={{ x: ['-1.5%', '1.5%', '-1.5%'], y: ['-1%', '1%', '-1%'] }}
          transition={{ duration: 46, repeat: Infinity, ease: 'easeInOut' }}
          onError={(e) => {
            e.currentTarget.style.opacity = 0;
          }}
        />
      </motion.div>

      {/* lit hide — same grain, brighter, revealed only where the pointer rakes */}
      {reactive && (
        <motion.div
          className="absolute inset-0 hidden md:block"
          style={{ maskImage: rake, WebkitMaskImage: rake }}
        >
          <motion.div className="absolute inset-0" style={{ y, scale }}>
            <motion.img
              src={src}
              alt=""
              className="w-full h-full object-cover"
              style={{
                opacity: Math.min(1, intensity * 1.35),
                filter: 'grayscale(1) brightness(2.35) contrast(1.15)',
              }}
              animate={{ x: ['-1.5%', '1.5%', '-1.5%'], y: ['-1%', '1%', '-1%'] }}
              transition={{ duration: 46, repeat: Infinity, ease: 'easeInOut' }}
              onError={(e) => {
                e.currentTarget.style.opacity = 0;
              }}
            />
          </motion.div>
        </motion.div>
      )}

      {/* legibility scrim — solid house colour, keeps type crisp over the grain */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: 'var(--mb-bg)', opacity: 0.42 }}
      />

      {/* palette wash — keeps the texture in the house colour in every theme */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: 'var(--mb-surface-rich)', opacity: 0.4, mixBlendMode: 'color' }}
      />

      {/* film grain */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: GRAIN_SVG,
          backgroundRepeat: 'repeat',
          opacity: 0.05,
          mixBlendMode: 'overlay',
        }}
      />

      {/* single hairline to anchor the band */}
      <div className="absolute left-0 right-0 bottom-0 h-[0.5px] bg-[var(--mb-border)]" />
    </div>
  );
};

export default LeatherBackdrop;
